import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { AMBASSADORS_PAGE_STATIC_KEY } from '../../../services/ambassadors/index'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'

function getPagesRange (page, totalPages) {
  if (totalPages < 10) return [1, totalPages]
  if (page < 6) return [1, 10]
  if (page + 4 > totalPages) return [totalPages - 9, totalPages]

  return [page - 5, page + 4]
}

/**
 * @param {Object} props - The props
 * @param {Boolean} isActive - is the current item active
 * @param {Number} page - number of the page
 * @param {String} label - label to show
 * @param {Boolean} isCurrentPage - is the item the current page
 * @param {Function} onChangePage - function than handles the page change
 * @returns {JSX.Element} - Page item element
 */
const PageItem = (props) => {
  const {
    isActive = true,
    page,
    label,
    isCurrentPage = false,
    onChangePage,
  } = props

  const changePage = useCallback(() => {
    onChangePage(page)
  }, [page, onChangePage])

  if (isActive) {
    const currentPageClass = isCurrentPage ? ' ambassadors-pager__page-current' : ''
    return (
      <div
        className={`ambassadors-pager__page ${currentPageClass} ambassadors-pager__page--active`}
        onClick={() => changePage()}
      >
        {label}
      </div>
    )
  }

  return (
    <div className="ambassadors-pager__page ambassadors-pager__page--inactive">
      {label}
    </div>
  )
}

/**
 * @param {Object} props - The props
 * @param {Function} onChangePage - is the current item active
 * @param {Number} pp - per page elements
 * @param {Number} p - page number
 * @param {Number} total - total number of elements
 * @returns {JSX.Element} - Pager
 */
const AmbassadorsPager = (props) => {
  const {
    onChangePage,
    pp,
    p = 1,
    total,
  } = props

  if (total === 0) {
    return null
  }

  const pages = Math.ceil(total / pp)
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))

  const renderNumericPages = () => {
    const pageRange = getPagesRange(p, pages)
    const numericPages = []

    for (let i = pageRange[0]; i <= pageRange[1]; i += 1) {
      numericPages.push(
        <PageItem
          key={`page-${i}`}
          className="ambassadors-pager__page-numeric"
          page={i}
          label={i}
          isCurrentPage={p === i}
          onChangePage={onChangePage}
        />,
      )
    }

    return numericPages
  }

  return (
    <div className="ambassadors-pager">
      <div className="ambassadors-pager__container">
        <PageItem
          isActive={p !== 1}
          page={1}
          label={staticText.get('first', '')}
          onChangePage={onChangePage}
        />
        <PageItem
          isActive={p > 1}
          page={p - 1}
          label={staticText.get('previous', '')}
          onChangePage={onChangePage}
        />
        <div className="ambassadors-pager__pages">
          { renderNumericPages() }
        </div>
        <PageItem
          isActive={p + 1 <= pages}
          page={p + 1}
          label={staticText.get('next', '')}
          onChangePage={onChangePage}
        />
        <PageItem
          isActive={p !== pages}
          page={pages}
          label={staticText.get('last', '')}
          onChangePage={onChangePage}
        />
      </div>
    </div>
  )
}

export default AmbassadorsPager
